import type { PostHogLike, Props } from "./types";
import type { TelemetryEventName } from "./events";

/**
 * Vendor- and platform-agnostic telemetry facade shared by every frontend.
 *
 * Each app creates its own PostHog client (`posthog-js` on web,
 * `posthog-react-native` on native) and calls `Telemetry.init(client)`. Until
 * then — or if the analytics key isn't configured — every call is a safe no-op,
 * so nothing tracks in dev/preview without a key and calls never throw.
 *
 * @example
 * import { Telemetry, TelemetryEvent } from '@linqx/telemetry';
 * Telemetry.init(posthog, { app: 'client-mobile' });
 * Telemetry.identify(user.id, { email: user.email });
 * Telemetry.track(TelemetryEvent.SWIPE_LIKE, { target: profileId });
 */
class TelemetryImpl {
  private client: PostHogLike | null = null;
  private common: Props = {};

  /** Wire a PostHog client. Safe to call once at app startup. */
  init(
    client: PostHogLike,
    opts?: { app?: string; commonProperties?: Props },
  ): void {
    this.client = client;
    this.common = { app: opts?.app, ...(opts?.commonProperties ?? {}) };
  }

  /** Whether a client is wired (analytics active). */
  get enabled(): boolean {
    return this.client !== null;
  }

  /**
   * Persist super-properties that ride on every subsequent event (e.g.
   * `platform`, `app_version`, `source`). Merged into the local common bag AND
   * registered on the underlying client so autocaptured events carry them too.
   */
  register(properties: Props): void {
    this.common = { ...this.common, ...properties };
    try {
      this.client?.register?.(properties);
    } catch {
      // ignore
    }
  }

  /** The current distinct id (anonymous before identify, user id after). */
  get distinctId(): string | undefined {
    try {
      return this.client?.getDistinctId?.() ?? this.client?.get_distinct_id?.();
    } catch {
      return undefined;
    }
  }

  /** Record a product event. No-op until init; never throws. */
  track(event: TelemetryEventName | string, properties?: Props): void {
    if (!this.client) return;
    try {
      this.client.capture(event, { ...this.common, ...(properties ?? {}) });
    } catch {
      // analytics must never break the app
    }
  }

  /**
   * Tie the current person to a stable id (always the **linqx user id**, never
   * the better-auth id — the backend keys server events on the linqx user id, so
   * this is what stitches client + server together).
   *
   * This is also the anonymous→identified bridge: PostHog automatically links
   * the pre-login anonymous distinct id to `distinctId`, so events captured
   * before signup are attributed to the new user. Call it right after signup /
   * session restore, and do NOT call `reset()` first (that would sever the anon
   * link).
   */
  identify(distinctId: string, traits?: Props): void {
    if (!this.client || !distinctId) return;
    try {
      this.client.identify(distinctId, traits);
    } catch {
      // ignore
    }
  }

  /**
   * Explicitly merge two identities. Use when a person already has events under
   * a different id (e.g. the better-auth id from auth-web) that must fold into
   * the canonical linqx user id. `identify()` already bridges the *anonymous*
   * id, so prefer that for the common signup case.
   *
   * @param userId - Canonical id to keep (linqx user id)
   * @param previousId - Prior id to merge in (defaults to the current anon id)
   */
  alias(userId: string, previousId?: string): void {
    if (!this.client || !userId) return;
    try {
      if (this.client.alias) {
        this.client.alias(userId, previousId);
      } else {
        // Client without alias() — identify still bridges the anon id.
        this.client.identify(userId);
      }
    } catch {
      // ignore
    }
  }

  /** Convenience screen-view event. */
  screen(name: string, properties?: Props): void {
    this.track("$screen", { screen: name, ...(properties ?? {}) });
  }

  /** Clear identity on sign-out. */
  reset(): void {
    if (!this.client) return;
    try {
      this.client.reset?.();
    } catch {
      // ignore
    }
  }
}

/** Shared singleton — import and use everywhere. */
export const Telemetry = new TelemetryImpl();
